import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { motion } from "framer-motion";
import { School, Send, LoaderCircle, MailCheck, Check } from "lucide-react";
import { ScrollReveal } from "./Reveal";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const initial = { scoala: "", nume: "", email: "", telefon: "", mesaj: "" };

const inputCls =
  "w-full bg-transparent border-b border-sumi/25 focus:border-hanko outline-none py-3.5 text-base text-sumi placeholder:text-charcoal/40 transition-colors duration-300 rounded-none";

const PERKS = [
  "Lecții demonstrative și ateliere de cultură japoneză pentru elevi",
  "Club de japoneză after-school, chiar în incinta școlii",
  "Curriculum adaptat vârstei, aliniat JLPT",
  "Caligrafie, origami, ceremonia ceaiului — cultura vine la pachet",
];

export default function Scoli() {
  const [form, setForm] = useState(initial);
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await axios.post(`${API}/trial-leads`, { ...form, tip: "parteneriat" });
      setDone(true);
      toast.success("Cererea de colaborare a fost trimisă!");
    } catch (err) {
      toast.error("Ceva n-a mers. Încearcă din nou sau scrie-ne pe email.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <section id="scoli" data-testid="scoli-section" className="relative py-20 sm:py-32 bg-washiaccent/50 border-y border-sumi/10 overflow-hidden">
      <span aria-hidden className="pointer-events-none select-none absolute -right-6 top-8 font-jp text-[12rem] leading-none text-sumi/[0.05] hidden lg:block">
        学
      </span>

      <div className="max-w-7xl mx-auto px-4 sm:px-8 grid lg:grid-cols-2 gap-14 lg:gap-20 items-start">
        <ScrollReveal>
          <p className="text-xs font-mono tracking-[0.3em] uppercase text-hanko mb-4">Pentru școli</p>
          <h2 className="font-serif text-3xl sm:text-4xl lg:text-5xl text-sumi leading-tight">
            Lecții de japoneză <span className="italic text-hanko">în școala ta.</span>
          </h2>
          <p className="mt-6 text-charcoal/80 leading-relaxed max-w-md">
            Aducem Japonia direct la elevii tăi. SORA colaborează cu școli din București
            pentru lecții, cluburi și ateliere de cultură japoneză — pentru toate vârstele.
          </p>
          <ul className="mt-8 space-y-4">
            {PERKS.map((p) => (
              <li key={p} className="flex items-start gap-3 text-sm sm:text-base text-charcoal/80">
                <span className="w-6 h-6 rounded-full bg-hanko/10 flex items-center justify-center shrink-0 mt-0.5">
                  <Check size={13} className="text-hanko" />
                </span>
                {p}
              </li>
            ))}
          </ul>
        </ScrollReveal>

        <ScrollReveal delay={0.15}>
          {done ? (
            <motion.div
              data-testid="scoli-success-state"
              initial={{ opacity: 0, scale: 0.96 }}
              animate={{ opacity: 1, scale: 1 }}
              className="bg-white border border-sumi/10 p-10 sm:p-14 text-center"
            >
              <span className="mx-auto w-16 h-16 rounded-full bg-hanko/10 flex items-center justify-center">
                <MailCheck size={30} className="text-hanko" />
              </span>
              <h3 className="mt-6 font-serif text-2xl sm:text-3xl text-sumi">Mulțumim!</h3>
              <p className="mt-4 text-charcoal/75 leading-relaxed max-w-sm mx-auto">
                Cererea de colaborare pentru <span className="font-semibold text-sumi">{form.scoala}</span> a ajuns la noi.
                Revenim pe email cu o propunere.
              </p>
              <p className="mt-6 font-jp text-hanko text-xl">よろしくお願いします</p>
            </motion.div>
          ) : (
            <form
              data-testid="scoli-partnership-form"
              onSubmit={submit}
              className="bg-white border border-sumi/10 p-8 sm:p-12"
            >
              <div className="flex items-center gap-3 mb-9">
                <span className="w-10 h-10 rounded-full bg-hanko/10 flex items-center justify-center">
                  <School size={18} className="text-hanko" />
                </span>
                <p className="font-serif text-xl text-sumi">Hai să colaborăm</p>
              </div>

              <div className="space-y-7">
                <div>
                  <label htmlFor="scoli-scoala" className="text-[11px] font-mono tracking-widest uppercase text-charcoal/60">Numele școlii *</label>
                  <input
                    id="scoli-scoala"
                    data-testid="scoli-form-input-school"
                    required
                    minLength={2}
                    value={form.scoala}
                    onChange={set("scoala")}
                    placeholder="Ex: Școala Gimnazială nr. …"
                    className={inputCls}
                  />
                </div>
                <div>
                  <label htmlFor="scoli-nume" className="text-[11px] font-mono tracking-widest uppercase text-charcoal/60">Persoană de contact *</label>
                  <input
                    id="scoli-nume"
                    data-testid="scoli-form-input-name"
                    required
                    minLength={2}
                    value={form.nume}
                    onChange={set("nume")}
                    placeholder="Numele tău"
                    className={inputCls}
                  />
                </div>
                <div className="grid sm:grid-cols-2 gap-7">
                  <div>
                    <label htmlFor="scoli-email" className="text-[11px] font-mono tracking-widest uppercase text-charcoal/60">Email *</label>
                    <input
                      id="scoli-email"
                      data-testid="scoli-form-input-email"
                      type="email"
                      required
                      value={form.email}
                      onChange={set("email")}
                      placeholder="scoala@email.com"
                      className={inputCls}
                    />
                  </div>
                  <div>
                    <label htmlFor="scoli-telefon" className="text-[11px] font-mono tracking-widest uppercase text-charcoal/60">Telefon *</label>
                    <input
                      id="scoli-telefon"
                      data-testid="scoli-form-input-phone"
                      type="tel"
                      required
                      minLength={6}
                      value={form.telefon}
                      onChange={set("telefon")}
                      placeholder="07xx xxx xxx"
                      className={inputCls}
                    />
                  </div>
                </div>
                <div>
                  <label htmlFor="scoli-mesaj" className="text-[11px] font-mono tracking-widest uppercase text-charcoal/60">Ce vă imaginați? (opțional)</label>
                  <textarea
                    id="scoli-mesaj"
                    data-testid="scoli-form-input-message"
                    rows={3}
                    value={form.mesaj}
                    onChange={set("mesaj")}
                    placeholder="Ex: un club săptămânal pentru gimnaziu…"
                    className={`${inputCls} resize-none`}
                  />
                </div>
              </div>

              <button
                data-testid="scoli-form-submit-button"
                type="submit"
                disabled={loading}
                className="mt-10 w-full bg-hanko text-washi font-semibold min-h-[56px] rounded-full hover:bg-hankodeep transition-colors duration-300 flex items-center justify-center gap-2 disabled:opacity-60"
              >
                {loading ? <LoaderCircle size={20} className="animate-spin" /> : <Send size={18} />}
                {loading ? "Se trimite…" : "Trimite cererea de colaborare"}
              </button>
              <p className="mt-5 text-xs text-charcoal/55 leading-relaxed text-center">
                Revenim pe email cu o propunere de colaborare pentru școala ta.
              </p>
            </form>
          )}
        </ScrollReveal>
      </div>
    </section>
  );
}
